import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import CallToAction from "../components/CallToAction";
import { Check } from "lucide-react";

// Team data
const team = [
  {
    id: 1,
    name: "Dr. Danijel Novak",
    role: "Specialist plastične, rekonstruktivne in estetske kirurgije",
    image: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    description: "Več kot 15 let izkušenj v plastični kirurgiji. Mednarodno izobražen strokovnjak za estetske in rekonstruktivne posege."
  },
  {
    id: 2,
    name: "Maja Kovač",
    role: "Medicinska sestra",
    image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    description: "Specializirana za nego po estetskih posegih z več kot 10 leti izkušenj pri delu s pacienti."
  },
  {
    id: 3,
    name: "Ana Horvat",
    role: "Administratorka",
    image: "https://images.unsplash.com/photo-1614644147724-2d4785d69962?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    description: "Skrbi za nemoten potek dela v kliniki in prijazno komunikacijo s pacienti od prvega stika dalje."
  },
  {
    id: 4,
    name: "Peter Vidmar",
    role: "Medicinski tehnik",
    image: "https://images.unsplash.com/photo-1537368910025-700350fe46c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80",
    description: "Izkušen medicinski tehnik z dodatnimi specializacijami za delo na področju estetske medicine."
  }
];

// Approach values
const values = [
  {
    id: 1,
    title: "Individualen pristop",
    description: "Vsak pacient je edinstven, zato so tudi naši pristopi in rešitve prilagojeni posamezniku."
  },
  {
    id: 2,
    title: "Varnost na prvem mestu",
    description: "Uporabljamo najsodobnejše tehnike in sledimo strogim varnostnim protokolom za najboljše rezultate."
  },
  {
    id: 3,
    title: "Celovita podpora",
    description: "Spremljamo celoten postopek od prvega posveta do popolnega okrevanja in po njem."
  },
  {
    id: 4,
    title: "Izobraževanje pacientov",
    description: "Verjamemo v informirano odločanje, zato si vzamemo čas za pojasnitev vseh vidikov postopka."
  }
];

const AboutPage = () => {
  return (
    <Layout>
      <PageHeader
        title="O nas"
        description="Spoznajte dr. Danijela Novaka, specialista plastične, rekonstruktivne in estetske kirurgije."
      />
      
      {/* Clinic Story Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Naša zgodba</h2>
            
            <div className="space-y-6 text-lg text-gray-700">
              <p>
                Klinika za estetsko in rekonstruktivno kirurgijo dr. Danijela Novaka je specializirana ustanova, 
                posvečena vrhunskim estetskim in rekonstruktivnim storitvam. Z ekipo strokovnjakov, ki jih vodi 
                dr. Danijel Novak, ponujamo celosten pristop k lepoti in dobremu počutju.
              </p>
              <p>
                Naša klinika je bila ustanovljena z vizijo ustvariti prostor, kjer se vrhunska medicinska 
                strokovnost srečuje z umetniškim občutkom za estetiko. Skozi leta smo pomagali številnim 
                pacientom doseči želeni videz in povečati samozavest, vedno z natančnostjo in spoštovanjem 
                do naravne lepote.
              </p>
              <p>
                Naša filozofija temelji na prepričanju, da je naravna lepota najboljša lepota. Z naprednimi 
                tehnikami in individualiziranim pristopom ustvarjamo rezultate, ki izgledajo naravno in 
                harmonično. Verjamemo, da je vsak pacient edinstven, zato so tudi naše rešitve prilagojene 
                posamezniku.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team Section */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">Naša ekipa</h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto text-center mb-12">
            Spoznajte našo ekipo predanih strokovnjakov, ki skrbijo za vaše dobro počutje in vrhunske rezultate.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member) => (
              <div key={member.id} className="glass-card p-6 transition-all duration-300 hover:translate-y-[-5px]">
                <div className="mb-4 rounded-2xl overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-full h-56 object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-medical-600 mb-3">{member.role}</p>
                <p className="text-gray-600">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Our Approach Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Naš pristop</h2>
            <p className="text-lg text-gray-700">
              V središču našega delovanja so visoki standardi strokovnosti, etičnosti in skrbi za pacienta. 
              Naš pristop temelji na vrednotah, ki nas vodijo pri vsakdanjem delu.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value) => (
              <div key={value.id} className="flex items-start">
                <div className="bg-medical-100 p-2 rounded-full text-medical-600 mr-4 mt-1">
                  <Check size={18} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-700">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-16 bg-gradient-to-br from-medical-50 to-medical-100/30 p-8 rounded-3xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white flex-shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80" 
                  alt="Dr. Danijel Novak" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-lg italic text-gray-700 mb-2">
                  "Moja vizija je ustvariti kliniko, kjer se vrhunska medicinska strokovnost združuje z estetskim občutkom. 
                  Vedno stremim k rezultatom, ki so harmonični, naravni in izboljšajo samopodobo naših pacientov."
                </p>
                <p className="font-bold text-gray-900">Dr. Danijel Novak</p>
                <p className="text-medical-600">Specialist plastične, rekonstruktivne in estetske kirurgije</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CallToAction />
    </Layout>
  );
};

export default AboutPage;